import { useSignUp } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { getConfig } from '../../config';
import { BadRequestError } from '../../error';
import { addTrackCrumb, trackError } from '../../error/tracking';
import { captureException } from '../../logger/capture-exception';
import { useTNamespace } from '../../services/translation';
import { tanstackLocationWithUtm } from '../../shared/utm';
import { AuthBody } from './-authbody';
import { SignUpStepOne } from './-signup-step-one';
import { SignUpWithEmail } from './-signup-with-email';
import { Verifying } from './-verifying';

const verifyCodeLength = getConfig().signupVerificationCodeLength;

export const Route = createFileRoute('/auth/signup')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/auth/signup',
      level: 'info',
    });
    addTrackCrumb({
      category: 'auth',
      message: 'inside signup',
      level: 'info',
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: Index,
});

function verifyUserFromSignupEmail(
  code: string,
  signUp: ReturnType<typeof useSignUp>['signUp'],
  setActive: ReturnType<typeof useSignUp>['setActive']
) {
  if (signUp == null || setActive == null) {
    return Promise.resolve(undefined);
  }

  return Promise.resolve().then(async function () {
    const signUpAttempt = await signUp.attemptEmailAddressVerification({ code });

    // If verification was completed, set the session to active
    // and redirect the user
    if (signUpAttempt.status === 'complete') {
      await setActive({ session: signUpAttempt.createdSessionId });
      return;
    }

    // If the status is not complete, check why. User may need to
    // complete further steps.
    throw new BadRequestError('unable to verify signup', { cause: signUpAttempt });
  });
}

function Index() {
  const t = useTNamespace('auth.signup');
  const { isLoaded, signUp, setActive } = useSignUp();
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isSendVerifyLoading, setIsSendVerifyLoading] = useState<boolean>(false);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const [pageTitle, setPageTitle] = useState<string | null>(null);

  useEffect(
    function () {
      if (isVerifyingCode) {
        setPageTitle(null);
        return;
      }
      setPageTitle(t('createAnAccount'));
    },
    [isVerifyingCode, email, t]
  );

  function sendVerifyEmail() {
    if (!isLoaded || signUp == null) {
      return Promise.resolve();
    }

    setIsSendVerifyLoading(true);

    addTrackCrumb({
      category: 'auth',
      message: 'sending verification email',
      level: 'info',
    });

    // Send the user an email with the verification code
    // TODO: will this actually work?!
    return signUp
      .prepareEmailAddressVerification({
        strategy: 'email_code',
      })
      .finally(function () {
        setIsSendVerifyLoading(false);
      });
  }

  return (
    <AuthBody
      showSignIn={!isVerifyingCode && (email ?? '').length == 0}
      isLoading={isSendVerifyLoading}
      pageTitle={pageTitle ?? undefined}
      centerContent={true}
      isSignup={true}
    >
      {!isVerifyingCode && (email ?? '').length === 0 ? (
        <SignUpStepOne setEmail={setEmail} />
      ) : null}
      {!isVerifyingCode && (email ?? '').length > 0 ? (
        <SignUpWithEmail
          email={email}
          sendBack={() => setEmail('')}
          setVerifying={() => {
            setEmail('');
            setIsVerifyingCode(true);
          }}
        />
      ) : null}
      {isVerifyingCode ? (
        <Verifying
          tryVerifyEmailAgain={sendVerifyEmail}
          codeLength={verifyCodeLength}
          email={email}
          processCode={function (code) {
            if (code != null && code.length > 0) {
              return verifyUserFromSignupEmail(code, signUp, setActive);
            }

            return Promise.resolve(undefined);
          }}
          nextStep={function () {
            // redirect the user for the waiting room
            navigate({ ...tanstackLocationWithUtm('/auth/wait-user'), replace: true }).catch(
              function (err) {
                captureException(
                  err,
                  {
                    file: 'auth.signup.tsx',
                    method: 'nextStep',
                    infoMessage: 'unable to navigate to wait-user',
                  },
                  trackError
                );
              }
            );
          }}
          sendBack={function () {
            navigate(tanstackLocationWithUtm('/auth/signup')).catch(function (err) {
              captureException(
                err,
                {
                  file: 'auth.signup.tsx',
                  method: 'sendBack',
                  infoMessage: 'unable to navigate to /auth/signup',
                },
                trackError
              );
            });
          }}
        />
      ) : null}
    </AuthBody>
  );
}
